import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { AuthenticationType, ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { AuthType } from 'app/extensibility/metadatas/control/azure-databricks-existing-workspace-cluster-select-metadata';
import { GlobalVariables } from 'app/services/globals';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const getWorkspaceResourceIdTextbox = (authType: AuthType): ControlMetadata => ({
    type: 'parameterizable',
    hide: () => authType === AuthType.token,
    child: {
        type: 'textbox',
        bind: 'workspaceResourceId',
        required: true,
        isLabelVertical: true,
        validators: [{ type: 'required', errorMessage: '' }],
        label: stringResources.ModelName_WorkspaceResourceId
    }
});

function accessTokenControl(authType: AuthType): ControlMetadata {
    return {
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{manualSelected ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-databricks-existing-workspace-cluster-select',
                        bind: {
                            domain: 'domain',
                            accessToken: 'accessToken',
                            credential: 'credential',
                            clusterId: 'clusterId',
                            workspaceResourceId: 'workspaceResourceId'
                        },
                        authType
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'domain',
                            required: true,
                            label: stringResources.DomainLabel,
                            isLabelVertical: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'clusterId',
                            required: true,
                            label: stringResources.ModelName_ExistingClusterId,
                            isLabelVertical: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        }
                    },
                    {
                        // Access token, visible for all old scenarios & AuthType.token under new scenario
                        type: 'dynamic-akv-switcher',
                        bind: 'accessToken',
                        enableAKVParameterization: true,
                        label: stringResources.ModelName_AccessToken,
                        hide: () => authType !== AuthType.token,
                        child: {
                            type: 'parameterizable',
                            attribute: 'accessToken',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_AccessToken,
                                required: true,
                                isLabelVertical: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        }
                    },
                    {
                        // Credential, visible only under new scenario for AuthType.uami
                        type: 'credential-select',
                        bind: 'credential',
                        hide: () => authType !== AuthType.uami,
                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    },
                    {
                        // MSI, visible only under new scenario for AuthType.msi
                        type: 'management-identity-info',
                        hide: () => authType !== AuthType.msi,
                        resourceType: stringResources.AzureDatabricksDeltaLake_DisplayText
                    },
                    {
                        // UserAuth, visible only under ff enableConnectorUserAuthCred & AuthType.userAuth
                        type: 'credential-select',
                        bind: 'credential',
                        hide: () => !(authType === AuthType.userAuth && GlobalVariables.enableConnectorUserAuthCred),
                        filterCredentialType: CredentialTypeOptions.UserAuth,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    },
                    // visible only under new scenario when not AuthType.token
                    getWorkspaceResourceIdTextbox(authType)
                ]
            }
        ]
    };
}

export const azureDatabricksDeltaLakeMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureDatabricksDeltaLake.linkedServiceType,
        displayName: stringResources.AzureDatabricksDeltaLake_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-20x20-delta-lake'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput:
            GlobalVariables.tridentBatch1ConnectorPhase2,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'Databricks' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Key],
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        notSupportFabricBuiltInStaging: true,
        isFirstClass: true,
        supportDatasetV2: true,
        supportFirstClassParameterExperience: true,
        copySourceTypes: [adfModelTypeMap.azureDatabricksDeltaLake.copySourceType],
        copySinkTypes: [adfModelTypeMap.azureDatabricksDeltaLake.copySinkType],
        category: ['Azure'],
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.Authentication_Method_Label,
                isLabelVertical: true,
                defaultValue: AuthenticationType.AccessToken,
                required: true,
                options: mergeUserAuthCredentialOption(
                    [
                        {
                            label: stringResources.ModelName_AccessToken,
                            value: AuthenticationType.AccessToken
                        },
                        {
                            label: stringResources.AuthenticationType_ManagedServiceIdentity,
                            value: AuthenticationType.ManagedServiceIdentity
                        },
                        {
                            label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                            value: AuthenticationType.UserAssignedManagedIdentity
                        }
                    ],
                    AuthenticationType.UserAuth
                ),
                cases: [
                    {
                        caseValue: AuthenticationType.AccessToken,
                        children: [accessTokenControl(AuthType.token)]
                    },
                    {
                        caseValue: AuthenticationType.ManagedServiceIdentity,
                        children: [accessTokenControl(AuthType.msi)]
                    },
                    {
                        caseValue: AuthenticationType.UserAuth,
                        children: [accessTokenControl(AuthType.userAuth)]
                    },
                    {
                        caseValue: AuthenticationType.UserAssignedManagedIdentity,
                        children: [accessTokenControl(AuthType.uami)]
                    }
                ]
            }
        ],
        fetchNestedNavTable: true
    };

    return metadata;
};
